import { RefObject, useEffect, useRef } from "react";
// Types only: Chart.js is in the lazily loaded chart chunk
import type { Chart } from "chart.js";
import { rewriteChartTooltipData } from "stores/genericStore/actions";
import {
  useGenericDispatch,
  useHoveredTrackpoint,
} from "stores/genericStore/GenericContext";
import { useRouting } from "stores/routingStore/RoutingContext";
import { ChartHoverFormatters, getChartHoverData } from "./chartHover";
import type { ChartDataPoint } from "./TrackChart";

/**
 * Shows the place of the track hovered on the map in the chart, as if the chart itself was hovered there: the
 * chart's tooltip and vertical bar at that distance or time, in between the track's points.
 * A separate component, so the hover doesn't rerender the chart.
 */
export const ChartHoverSync = ({
  chartRef,
  points,
  formatters,
}: {
  chartRef: RefObject<Chart<"line", ChartDataPoint[], unknown> | null>;
  /** The chart's points, one for each track point */
  points: ChartDataPoint[];
  formatters: ChartHoverFormatters;
}) => {
  const hoveredTrackpoint = useHoveredTrackpoint();
  const dispatch = useGenericDispatch();
  const {
    state: { computedTrackpoints },
  } = useRouting();
  // Whether the chart shows a place hovered on the map, so only that is cleared, not the chart's own hover
  const isShowingRef = useRef(false);

  useEffect(() => {
    const chart = chartRef.current;
    const hover =
      chart && hoveredTrackpoint
        ? getChartHoverData({
            chart,
            points,
            trackpoints: computedTrackpoints,
            place: hoveredTrackpoint,
            formatters,
            isHovered: true,
          })
        : null;

    if (hover) {
      isShowingRef.current = true;
      dispatch(rewriteChartTooltipData(hover));
    } else if (isShowingRef.current) {
      isShowingRef.current = false;
      dispatch(rewriteChartTooltipData(null));
    }
  }, [
    chartRef,
    computedTrackpoints,
    dispatch,
    formatters,
    hoveredTrackpoint,
    points,
  ]);

  return null;
};
